import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Button,
  Stack,
  Typography,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogContent,
  DialogActions,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/CloseOutlined";
import AutoFixIcon from "@mui/icons-material/AutoFixHighOutlined";
import RouterIcon from "@mui/icons-material/RouterOutlined";
import EditIcon from "@mui/icons-material/EditOutlined";
import FingerprintIcon from "@mui/icons-material/FingerprintOutlined";
import LabelIcon from "@mui/icons-material/LabelOutlined";
import CategoryIcon from "@mui/icons-material/CategoryOutlined";
import MemoryIcon from "@mui/icons-material/MemoryOutlined";
import CloudIcon from "@mui/icons-material/CloudOutlined";
import BusinessIcon from "@mui/icons-material/ApartmentOutlined";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import {
  createCompanyDevice,
  updateCompanyDevice,
  fetchCompanyDevices,
  lookupImei,
  assignDeviceToCompany,
  fetchNextServerId,
  clearImeiConflict,
} from "./companyDevicesSlice";
import {
  createStandaloneDevice,
  fetchAllDevices,
} from "../devices/devicesSlice";
import DangerConfirmField, {
  isDangerCodeValid,
} from "../../components/common/DangerConfirmField";
const MOTOR_MODELS = [
  "IOT-INVETER",
  "IOT-EX-DOL",
  "IOT-DOL-CAPCITOR",
  "IOT-DOL",
];
const TANK_MODELS = [
  "Float sensor",
  "Submersible Liquid Sensor",
  "Float +Submersible Liquid Sensor",
];

const DEVICE_TYPE_OPTIONS = [
  { value: 1, label: "Motor" },
  { value: 2, label: "Water Tank" },
  { value: 3, label: "Valve" },
  { value: 4, label: "Flow Meter" },
];

const modelOptionsFor = (type) => {
  if (Number(type) === 1) return MOTOR_MODELS;
  if (Number(type) === 2) return TANK_MODELS;
  return [];
};

const defaultModelFor = (type) => modelOptionsFor(type)[0] || "";

const fieldSx = {
  "& .MuiInputBase-input": { fontSize: "0.875rem" },
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: "background.paper",
  },
};

const validation = Yup.object({
  IMEI: Yup.string()
    .trim()
    .required("IMEI is required")
    .max(15, "Max 15 chars")
    .matches(/^[A-Za-z0-9]+$/, "Letters/numbers only"),
  device_type: Yup.number().oneOf([1, 2, 3, 4]).required(),
  serverId: Yup.mixed().when("sendDataToServer", {
    is: true,
    then: () =>
      Yup.number()
        .typeError("Number")
        .integer("Integer")
        .min(50500, "Min 50500")
        .max(52000, "Max 52000")
        .required("Required when Send Data is on"),
    otherwise: () => Yup.mixed().notRequired(),
  }),
});

const buildInitial = (device) => ({
  IMEI: device?.IMEI || "",
  device_name: device?.device_name || "",
  device_type: device?.device_type ?? 1,
  model: device?.model || defaultModelFor(device?.device_type ?? 1),
  sendDataToServer: Boolean(device?.sendDataToServer),
  serverId: device?.serverId ?? "",
});

const SectionLabel = ({ children }) => (
  <Typography
    variant="caption"
    sx={{
      display: "block",
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      color: "text.secondary",
      fontSize: "0.6875rem",
      mb: 1,
    }}
  >
    {children}
  </Typography>
);

// `mode` controls which create endpoint + post-save refresh fires:
//   - "company"   (default) — original per-company tab behavior. Form is
//                              pinned to `companyId` from the route.
//   - "standalone"          — global /devices page. Create hits
//                              POST /api/devices (no company attached).
//
// Either mode supports editing existing devices — only IMEI/name/type/model/
// server-sync fields are editable. Linking a standalone device to a company
// happens through the per-company "Add Device" flow, not from here.
export default function DeviceForm({
  companyId,
  editingDevice,
  onClose,
  mode = "company",
}) {
  const dispatch = useDispatch();
  const { saving: companySaving, imeiLookup } = useSelector(
    (s) => s.companyDevices
  );
  const { saving: devicesSaving } = useSelector((s) => s.devices);
  const isEdit = Boolean(editingDevice);
  const isStandalone = mode === "standalone";
  // Both slices have their own `saving` flag; UI just needs "is any save
  // currently in flight". Use this alias everywhere below.
  const saving = isStandalone ? devicesSaving || companySaving : companySaving;
  const debounceRef = useRef(null);
  // "Assign to this company" dialog state. Only used in per-company create
  // mode when the IMEI lookup turns up an existing device that lives on a
  // different company — we then require the security code before
  // displacing it. (Standalone → this-company assign skips the dialog
  // entirely and fires immediately on button click.)
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [assignConfirm, setAssignConfirm] = useState("");

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: buildInitial(editingDevice),
    validationSchema: validation,
    onSubmit: async (values) => {
      const conflict = imeiLookup.conflict;
      // Per-company create: any IMEI that already exists must be resolved
      // via the alert (Assign here / open existing) rather than POSTed.
      if (!isEdit && !isStandalone && conflict) {
        toast.error(
          "Resolve the IMEI conflict via the banner above first."
        );
        return;
      }

      const payload = {
        IMEI: values.IMEI.trim().replace(/\s+/g, ""),
        device_name: values.device_name?.trim() || undefined,
        device_type: Number(values.device_type),
        model:
          Number(values.device_type) === 1 || Number(values.device_type) === 2
            ? values.model
            : "",
        sendDataToServer: Boolean(values.sendDataToServer),
        serverId: values.sendDataToServer
          ? Number(values.serverId)
          : undefined,
      };

      let action;
      if (isEdit) {
        action = updateCompanyDevice({ id: editingDevice._id, payload });
      } else if (isStandalone) {
        action = createStandaloneDevice(payload);
      } else {
        action = createCompanyDevice({ companyId, payload });
      }
      const res = await dispatch(action);
      const ok = isEdit
        ? updateCompanyDevice.fulfilled.match(res)
        : isStandalone
          ? createStandaloneDevice.fulfilled.match(res)
          : createCompanyDevice.fulfilled.match(res);

      if (ok) {
        toast.success(isEdit ? "Device updated" : "Device created");
        dispatch(clearImeiConflict());
        if (isStandalone) {
          dispatch(fetchAllDevices());
        } else if (companyId) {
          dispatch(fetchCompanyDevices({ companyId }));
        }
        onClose();
      } else {
        toast.error(res.payload || "Save failed");
      }
    },
  });

  useEffect(() => {
    return () => dispatch(clearImeiConflict());
  }, [dispatch]);

  useEffect(() => {
    const imei = (formik.values.IMEI || "").trim().replace(/\s+/g, "");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (isEdit) {
      dispatch(clearImeiConflict());
      return;
    }
    if (imei.length < 5) {
      dispatch(clearImeiConflict());
      return;
    }
    debounceRef.current = setTimeout(() => {
      dispatch(lookupImei(imei));
    }, 400);
    // eslint-disable-next-line
  }, [formik.values.IMEI, isEdit]);

  const conflict = imeiLookup.conflict;
  // Any pre-existing IMEI blocks save during create — different states get
  // different affordances rendered in the alert below.
  const isConflict = !isEdit && Boolean(conflict);
  // Per-company create: classify the existing device so the alert can offer
  // the right action (assign here, or edit the one that's already here).
  const conflictCompanyId = conflict?.company?._id || null;
  const conflictIsSameCompany =
    !isStandalone && conflictCompanyId && conflictCompanyId === companyId;
  const conflictIsStandalone = !isStandalone && conflict && !conflictCompanyId;
  const conflictIsOtherCompany =
    !isStandalone &&
    conflictCompanyId &&
    conflictCompanyId !== companyId;

  const onTypeChange = (e) => {
    const t = Number(e.target.value);
    formik.setFieldValue("device_type", t);
    const opts = modelOptionsFor(t);
    if (opts.length && !opts.includes(formik.values.model)) {
      formik.setFieldValue("model", opts[0]);
    } else if (!opts.length) {
      formik.setFieldValue("model", "");
    }
  };

  const fillNextServerId = async () => {
    const res = await dispatch(fetchNextServerId());
    if (fetchNextServerId.fulfilled.match(res)) {
      formik.setFieldValue("serverId", res.payload);
    } else {
      toast.error(res.payload || "Allocation failed");
    }
  };

  // Shared finalize step for both assign paths (standalone-fast-path and
  // confirmed-via-dialog). Calls PATCH /api/devices/:id { company: <here> }
  // — the backend cascade moves the device, clears old admins, and sweeps
  // device_order configs. Form closes on success; the parent list refetches.
  const performAssign = async () => {
    if (!conflict?._id) return;
    setAssigning(true);
    const res = await dispatch(
      assignDeviceToCompany({ id: conflict._id, companyId })
    );
    setAssigning(false);
    if (assignDeviceToCompany.fulfilled.match(res)) {
      toast.success(
        `${conflict.device_name || conflict.IMEI} assigned to this company`
      );
      dispatch(clearImeiConflict());
      dispatch(fetchCompanyDevices({ companyId }));
      setAssignDialogOpen(false);
      setAssignConfirm("");
      onClose();
    } else {
      toast.error(res.payload || "Assign failed");
    }
  };

  // Click handler on the alert button. Standalone device → fire immediately
  // (no displacement, no security code). Other-company device → open the
  // security-code dialog first.
  const onClickAssign = () => {
    if (conflictIsStandalone) {
      performAssign();
    } else {
      setAssignDialogOpen(true);
    }
  };

  const onConfirmAssign = async () => {
    if (!isDangerCodeValid(assignConfirm)) return;
    await performAssign();
  };

  const closeAssignDialog = () => {
    if (assigning) return;
    setAssignDialogOpen(false);
    setAssignConfirm("");
  };

  return (
    <>
      <Dialog
        open
        onClose={saving ? undefined : onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "14px",
            mx: 2,
            width: { xs: "calc(100% - 32px)", sm: "100%" },
            overflow: "hidden",
          },
        }}
      >
        {/* Gradient header band — matches the polished User dialog */}
        <Box
          sx={{
            position: "relative",
            px: 2,
            pt: 2.5,
            pb: 2,
            background:
              "linear-gradient(135deg, #0d9488 0%, #14b8a6 50%, #2dd4bf 100%)",
            color: "common.white",
          }}
        >
          <IconButton
            onClick={onClose}
            disabled={saving}
            size="small"
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "common.white",
              backgroundColor: "rgba(255,255,255,0.15)",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.25)" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "12px",
                bgcolor: "rgba(255,255,255,0.22)",
                color: "common.white",
                border: "2px solid rgba(255,255,255,0.35)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {isEdit ? (
                <EditIcon />
              ) : (
                <RouterIcon />
              )}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "1rem",
                  letterSpacing: "-0.01em",
                  lineHeight: 1.15,
                }}
              >
                {isEdit ? "Edit Device" : "New Device"}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  opacity: 0.92,
                  mt: 0.25,
                  wordBreak: "break-word",
                }}
              >
                {isEdit
                  ? `${editingDevice?.device_name || editingDevice?.IMEI || ""}`
                  : "Register a new IMEI and choose its type & model"}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <DialogContent sx={{ p: 0 }}>
          {isConflict && !isStandalone && (
            <Alert
              severity={conflictIsSameCompany ? "info" : "warning"}
              sx={{
                m: 2,
                mb: 0,
                py: 0.5,
                alignItems: "center",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
                sx={{ width: "100%" }}
              >
                <Typography variant="caption">
                  {conflictIsSameCompany && (
                    <>
                      IMEI <b>{conflict.IMEI}</b> is already in this company.
                      Edit it from the list below.
                    </>
                  )}
                  {conflictIsStandalone && (
                    <>
                      IMEI <b>{conflict.IMEI}</b> exists as a standalone
                      device. Assign it to this company?
                    </>
                  )}
                  {conflictIsOtherCompany && (
                    <>
                      IMEI <b>{conflict.IMEI}</b> currently belongs to{" "}
                      <b>{conflict.company?.name || "another company"}</b>.
                      Assigning it here will remove it from there.
                    </>
                  )}
                </Typography>
                {(conflictIsStandalone || conflictIsOtherCompany) && (
                  <Button
                    size="small"
                    variant="contained"
                    color={conflictIsStandalone ? "primary" : "warning"}
                    onClick={onClickAssign}
                    disabled={assigning}
                    startIcon={
                      assigning ? (
                        <CircularProgress size={12} color="inherit" />
                      ) : null
                    }
                    sx={{ fontSize: "0.6875rem", py: 0.25, flexShrink: 0 }}
                  >
                    {assigning ? "Assigning…" : "Assign to this company"}
                  </Button>
                )}
              </Stack>
            </Alert>
          )}

          <form id="device-form" onSubmit={formik.handleSubmit}>
            {/* === Identity section === */}
            <Box sx={{ p: 2 }}>
              <SectionLabel>Identity</SectionLabel>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="IMEI *"
                    name="IMEI"
                    placeholder="IMEI"
                    value={formik.values.IMEI}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.IMEI && Boolean(formik.errors.IMEI)}
                    helperText={formik.touched.IMEI && formik.errors.IMEI}
                    disabled={isEdit}
                    sx={fieldSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <FingerprintIcon
                            sx={{ fontSize: 18, color: "text.secondary" }}
                          />
                        </InputAdornment>
                      ),
                      endAdornment: imeiLookup.checking ? (
                        <CircularProgress size={14} />
                      ) : null,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Display Name"
                    name="device_name"
                    placeholder="Display name"
                    value={formik.values.device_name}
                    onChange={formik.handleChange}
                    sx={fieldSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LabelIcon
                            sx={{ fontSize: 18, color: "text.secondary" }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </Box>

            {isStandalone && (
              <>
                <Box sx={{ borderTop: "1px solid", borderColor: "divider" }} />
                {/* === Company (read-only) ===
                    Just shows the current assignment for context. Linking /
                    unlinking a device to a company is done via the
                    per-company "Add Device" flow, not here. */}
                <Box sx={{ p: 2 }}>
                  <SectionLabel>Company</SectionLabel>
                  {(() => {
                    // editingDevice.company on the /devices page is populated
                    // ({ _id, name, manufacturing_id }) when set, or null.
                    const c = editingDevice?.company;
                    const linked = c && typeof c === "object" && c.name;
                    const display = linked
                      ? c.manufacturing_id
                        ? `${c.name} · #${c.manufacturing_id}`
                        : c.name
                      : "— not assigned —";
                    return (
                      <TextField
                        fullWidth
                        size="small"
                        value={display}
                        InputProps={{
                          readOnly: true,
                          startAdornment: (
                            <InputAdornment position="start">
                              <BusinessIcon
                                sx={{ fontSize: 18, color: "text.secondary" }}
                              />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          ...fieldSx,
                          "& .MuiInputBase-input": {
                            ...fieldSx["& .MuiInputBase-input"],
                            color: linked ? "text.primary" : "text.disabled",
                            fontStyle: linked ? "normal" : "italic",
                          },
                        }}
                        helperText="Link / unlink from the company's Devices tab."
                      />
                    );
                  })()}
                </Box>
              </>
            )}

            <Box sx={{ borderTop: "1px solid", borderColor: "divider" }} />

            {/* === Type & Model section === */}
            <Box sx={{ p: 2 }}>
              <SectionLabel>Type &amp; Model</SectionLabel>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    select
                    label="Device Type"
                    name="device_type"
                    value={formik.values.device_type}
                    onChange={onTypeChange}
                    sx={fieldSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CategoryIcon
                            sx={{ fontSize: 18, color: "text.secondary" }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  >
                    {DEVICE_TYPE_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value} sx={{ fontSize: "0.8125rem" }}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={8}>
                  {modelOptionsFor(formik.values.device_type).length ? (
                    <TextField
                      fullWidth
                      size="small"
                      select
                      label="Model"
                      name="model"
                      value={formik.values.model || ""}
                      onChange={formik.handleChange}
                      sx={fieldSx}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MemoryIcon
                              sx={{ fontSize: 18, color: "text.secondary" }}
                            />
                          </InputAdornment>
                        ),
                      }}
                    >
                      {modelOptionsFor(formik.values.device_type).map((m) => (
                        <MenuItem key={m} value={m} sx={{ fontSize: "0.8125rem" }}>
                          {m}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : (
                    <TextField
                      fullWidth
                      size="small"
                      label="Model"
                      disabled
                      value="—"
                      helperText="No models available for this type"
                      sx={fieldSx}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MemoryIcon
                              sx={{ fontSize: 18, color: "text.secondary" }}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ borderTop: "1px solid", borderColor: "divider" }} />

            {/* === Server Sync section === */}
            <Box sx={{ p: 2 }}>
              <SectionLabel>Server Sync</SectionLabel>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", sm: "center" }}
              >
                <FormControlLabel
                  sx={{ m: 0, minWidth: 200 }}
                  control={
                    <Switch
                      size="small"
                      checked={Boolean(formik.values.sendDataToServer)}
                      onChange={(e) =>
                        formik.setFieldValue("sendDataToServer", e.target.checked)
                      }
                    />
                  }
                  label={
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: "0.8125rem", fontWeight: 500 }}
                      >
                        Send data to server
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: "0.6875rem" }}
                      >
                        Forward telemetry to remote endpoint
                      </Typography>
                    </Box>
                  }
                />
                <Stack direction="row" spacing={0.5} alignItems="flex-start" sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Server ID"
                    name="serverId"
                    type="number"
                    value={formik.values.serverId}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={!formik.values.sendDataToServer}
                    error={formik.touched.serverId && Boolean(formik.errors.serverId)}
                    helperText={
                      (formik.touched.serverId && formik.errors.serverId) ||
                      (formik.values.sendDataToServer
                        ? "Range 50500–52000"
                        : "")
                    }
                    sx={fieldSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CloudIcon
                            sx={{ fontSize: 18, color: "text.secondary" }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Tooltip title="Allocate next free server ID">
                    <span>
                      <IconButton
                        size="small"
                        onClick={fillNextServerId}
                        disabled={!formik.values.sendDataToServer}
                        sx={{ mt: 0.5 }}
                      >
                        <AutoFixIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </Stack>
            </Box>
          </form>
        </DialogContent>

        <DialogActions
          sx={{
            p: 1.5,
            borderTop: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
            gap: 1,
          }}
        >
          <Button
            onClick={onClose}
            variant="outlined"
            disabled={saving}
            sx={{ borderRadius: "8px", flex: { xs: 1, sm: "0 0 auto" } }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="device-form"
            variant="contained"
            disabled={saving || isConflict}
            startIcon={
              saving ? <CircularProgress size={14} color="inherit" /> : null
            }
            sx={{
              borderRadius: "8px",
              flex: { xs: 1, sm: "0 0 auto" },
              boxShadow: "0 4px 10px -4px rgba(13,148,136,0.55)",
            }}
          >
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Device"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={assignDialogOpen}
        onClose={closeAssignDialog}
        PaperProps={{ sx: { borderRadius: "10px", minWidth: { xs: 0, sm: 380 } } }}
        fullWidth
      >
        <Box
          sx={{
            p: 1.75,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, color: "warning.main" }}>
            Assign to this company
          </Typography>
        </Box>
        <DialogContent sx={{ p: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Move <b>{conflict?.IMEI}</b> from{" "}
            <b>{conflict?.company?.name || "the other company"}</b> to this
            company?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The device record, Powermeter row, and IMEI configuration are
            kept. Only the company link, station, and admin assignments
            change. The other company's admins will lose access; their
            device-order lists are renumbered.
          </Typography>
          <DangerConfirmField
            value={assignConfirm}
            onChange={setAssignConfirm}
            disabled={assigning}
          />
        </DialogContent>
        <DialogActions sx={{ p: 1.25, borderTop: "1px solid", borderColor: "divider" }}>
          <Button
            onClick={closeAssignDialog}
            variant="outlined"
            disabled={assigning}
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirmAssign}
            variant="contained"
            color="warning"
            disabled={assigning || !isDangerCodeValid(assignConfirm)}
            startIcon={assigning ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {assigning ? "Assigning…" : "Assign"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
